/**
 * Preview Questions Results Component
 * Displays questions using the Nagwa Questions Engine
 */

import { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useResources } from "../../hooks/useResources";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../contexts/ToastContext";
import { useQuestionStore } from "../../stores/question.store";
import ContentHeader from "../contentHeader/ContentHeader";
import Checkbox from "../checkbox/Checkbox";
import QuestionRenderer from "../questionRenderer/QuestionRenderer";
import PreviewPagination from "./PreviewPagination";
import DeleteModal from "../modal/DeleteModal";
import { downloadQuestionPackage, getQuestionPresignedUrls } from "../../services/question.service";
import { globalConfig } from "../../config/globalConfig";
import type { PreviewNavigationState } from "../../interfaces/componentInterfaces/questionPreview";
import type { QuestionMetadata } from "../../interfaces/componentInterfaces/questionRenderer";
import "./PreviewQuestionsResults.scss";

const PAGE_SIZE = globalConfig.previewQuestionsPageSize;

const PreviewQuestionsResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { questionId: urlQuestionId } = useParams<{ questionId?: string }>();
  const { getResourceValue } = useResources();
  const { canDeleteQuestions } = usePermissions();
  const { showError, showSuccess } = useToast();
  const { deleteQuestions } = useQuestionStore();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<string>>(new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [metadataMap, setMetadataMap] = useState<Record<string, QuestionMetadata>>({});
  const [presignedUrlMap, setPresignedUrlMap] = useState<Record<string, string>>({});

  const state = location.state as PreviewNavigationState | null;
  
  // Get question IDs from either URL param (manual) or state (CSV)
  // Make it stateful so we can update it after deletion
  const [questionIds, setQuestionIds] = useState<string[]>(() => {
    return urlQuestionId 
      ? [urlQuestionId] 
      : (state?.questionIds || []);
  });
  
  // Get jobId from query string or state (fallback for CSV uploads)
  const searchParams = new URLSearchParams(location.search);
  const jobIdFromUrl = searchParams.get('jobId');
  const jobId = jobIdFromUrl || state?.jobId;

  // Clear state after reading it to avoid persisting on refresh
  useEffect(() => {
    if (state) {
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  // Redirect if no question IDs provided
  if (!questionIds || questionIds.length === 0) {
    navigate("/questions/preview");
    return null;
  }

  // Pagination calculations
  const totalPages = Math.ceil(questionIds.length / PAGE_SIZE);
  const paginatedQuestionIds = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return questionIds.slice(startIndex, endIndex);
  }, [questionIds, currentPage]);

  // Calculate the starting index for question numbering
  const startingIndex = (currentPage - 1) * PAGE_SIZE;

  // Batch fetch presigned URLs for the current page of questions
  useEffect(() => {
    const fetchPresignedUrls = async () => {
      if (paginatedQuestionIds.length === 0) return;

      try {
        const objectKeys = paginatedQuestionIds.map(id => 
          jobId
            ? `jobs/${jobId}/questions/${id}/package/${id}.zip`
            : `packages/${id}.zip`
        );
        const response = await getQuestionPresignedUrls(objectKeys);
        const urlMap: Record<string, string> = {};
        response.forEach(item => {
          // Extract questionId from object_key (12-digit ID before .zip)
          const match = item.object_key.match(/(\d{12})\.zip$/);
          if (match) {
            urlMap[match[1]] = item.presigned_url;
          }
        });
        setPresignedUrlMap(urlMap);
      } catch (error) {
        console.error("Error fetching presigned URLs:", error);
        // Clear the map so QuestionRenderer falls back to individual fetching
        setPresignedUrlMap({});
      }
    };

    fetchPresignedUrls();
  }, [paginatedQuestionIds, jobId]);

  // Check if all questions on current page are selected
  const areAllSelectedOnPage = useMemo(() => {
    if (paginatedQuestionIds.length === 0) return false;
    return paginatedQuestionIds.every((id) => selectedQuestionIds.has(id));
  }, [paginatedQuestionIds, selectedQuestionIds]);

  // Determine if delete selected button should be enabled
  const isDeleteSelectedEnabled = selectedQuestionIds.size > 0 && !isDeleting;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleQuestionSelect = (id: string, selected: boolean) => {
    setSelectedQuestionIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedQuestionIds((prev) => {
        const newSet = new Set(prev);
        paginatedQuestionIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    } else {
      setSelectedQuestionIds((prev) => {
        const newSet = new Set(prev);
        paginatedQuestionIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  };

  const handleDeleteQuestion = (id: string) => {
    if (isDeleting) return;
    setQuestionToDelete(id);
    setIsBulkDelete(false);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSelected = () => {
    if (!isDeleteSelectedEnabled || selectedQuestionIds.size === 0) return;
    setQuestionToDelete(null);
    setIsBulkDelete(true);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setQuestionToDelete(null);
    setIsBulkDelete(false);
  };

  const handleConfirmDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        // Bulk delete selected questions
        const questionIdsArray = Array.from(selectedQuestionIds);
        const response = await deleteQuestions(questionIdsArray);
        
        // Remove successfully deleted questions from the list
        const deletedIds = response.details
          .filter((d) => d.success)
          .map((d) => d.question_id);
        
        setQuestionIds((prev) => prev.filter((qId) => !deletedIds.includes(qId)));
        setSelectedQuestionIds(new Set());
        
        // Adjust page if needed
        const newTotalPages = Math.ceil((questionIds.length - deletedIds.length) / PAGE_SIZE);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(newTotalPages);
        }

        // Show success message with details
        if (response.failed_count === 0) {
          showSuccess(
            getResourceValue("success", "Success"),
            getResourceValue(
              "questions_deleted_successfully",
              `${response.deleted_count} question(s) deleted successfully.`
            )
          );
        } else {
          // Build detailed error message with specific errors
          const failedDetails = response.details
            .filter((d) => !d.success)
            .map((d) => `Question ${d.question_id}: ${d.error || "Failed to delete"}`)
            .join(", ");
          
          showError(
            getResourceValue("delete_partial_error", "Delete Partially Failed"),
            `${getResourceValue("deleted_count", "Deleted")}: ${response.deleted_count}, ${getResourceValue("failed_count", "Failed")}: ${response.failed_count}. ${failedDetails}`
          );
        }
      } else if (questionToDelete) {
        // Single question delete
        const response = await deleteQuestions([questionToDelete]);
        
        // Check if the specific question was deleted successfully
        const questionResult = response.details.find((d) => d.question_id === questionToDelete);
        if (questionResult?.success) {
          // Remove the question from the list
          setQuestionIds((prev) => prev.filter((qId) => qId !== questionToDelete));
          setSelectedQuestionIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(questionToDelete);
            return newSet;
          });
          
          // Adjust page if needed
          const newTotalPages = Math.ceil((questionIds.length - 1) / PAGE_SIZE);
          if (currentPage > newTotalPages && newTotalPages > 0) {
            setCurrentPage(newTotalPages);
          }

          showSuccess(
            getResourceValue("success", "Success"),
            getResourceValue("question_deleted_successfully", "Question deleted successfully")
          );
        } else {
          const errorMessage = questionResult?.error || 
            getResourceValue("delete_question_failed", "Failed to delete question. Please try again.");
          showError(
            getResourceValue("error", "Error"),
            errorMessage
          );
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        getResourceValue("delete_questions_failed", "Failed to delete questions. Please try again.");
      showError(
        getResourceValue("error", "Error"),
        errorMessage
      );
      console.error("Error deleting questions:", error);
    } finally {
      setIsDeleting(false);
      handleCloseDeleteModal();
    }
  };

  const handleMetadataLoaded = useCallback((questionId: string, metadata: QuestionMetadata) => {
    setMetadataMap((prev) => {
      if (prev[questionId]) return prev;
      return { ...prev, [questionId]: metadata };
    });
  }, []);

  const handleDownloadZip = async () => {
    if (!questionIds || questionIds.length === 0 || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      const { download_url } = await downloadQuestionPackage(
        questionIds,
        jobId ?? null
      );
      
      if (download_url) {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = download_url;
        link.download = ''; // Suggests to download rather than navigate
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading questions package:', error);
      showError(
        getResourceValue("error", "Error"),
        getResourceValue("download_failed", "Failed to start download. Please try again.")
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="preview-questions-results">
      <div className="preview-questions-results__header">
        <ContentHeader
          title={getResourceValue("imported_questions","Imported Questions")}
        />
        <div className="preview-questions-results__header-actions">
          {canDeleteQuestions && (
            <button 
              className="btn btn-red delete-selected-btn" 
              onClick={handleDeleteSelected}
              disabled={!isDeleteSelectedEnabled}
            >
              <i className="f-icon delete-icon"></i>
              {isDeleting 
                ? getResourceValue("deleting", "Deleting...") 
                : getResourceValue("delete_selected", "Delete Selected")}
              {selectedQuestionIds.size > 0 && ` [${selectedQuestionIds.size}]`}
            </button>
          )}
          <button 
            className="btn btn-green download-btn" 
            onClick={handleDownloadZip}
            disabled={isDownloading || questionIds.length === 0}
          >
            <i className="f-icon download-icon"></i>
            {isDownloading 
              ? getResourceValue("downloading", "Downloading...") 
              : getResourceValue("download_zip_file", "Download ZIP File")}
          </button>
        </div>
      </div>

      <div className="preview-questions-results__content">
        {canDeleteQuestions && (
          <div className="preview-questions-results__select-all">
            <Checkbox
              id="select-all-questions"
              checked={areAllSelectedOnPage}
              onChange={handleSelectAll}
            />
            <label htmlFor="select-all-questions" className="preview-questions-results__select-all-label">
              {getResourceValue("select_all", "Select All")}
            </label>
          </div>
        )}
        <div className="questions-list">
          {paginatedQuestionIds.map((id, index) => (
            <div key={id} className="question-item">
              <div className="question-item__header">
                <div className="question-item__header-left">
                  {canDeleteQuestions && (
                    <Checkbox
                      id={`question-checkbox-${id}`}
                      checked={selectedQuestionIds.has(id)}
                      onChange={(checked) => handleQuestionSelect(id, checked)}
                    />
                  )}
                  <h3 className="question-item__title">
                    {getResourceValue("question", "Question")} {startingIndex + index + 1}{" "}
                    <span className="question-item__id">({id})</span>
                  </h3>
                </div>
                {canDeleteQuestions && (
                  <button
                    className="btn btn-red btn-sm question-item__delete-btn"
                    onClick={() => handleDeleteQuestion(id)}
                    disabled={isDeleting}
                    aria-label={getResourceValue("delete_question", "Delete Question")}
                  >
                    <i className="f-icon delete-icon"></i>
                    {getResourceValue("delete", "Delete")}
                  </button>
                )} 
              </div>
              {metadataMap[id] && (
                <div className="question-item__metadata">
                  <div className="question-item__metadata-field">
                    <span className="question-item__metadata-label">
                      {getResourceValue("parent_id", "Parent ID")}
                    </span>
                    <span className="question-item__metadata-value">
                      {metadataMap[id].parentId || "—"}
                    </span>
                  </div>
                  <div className="question-item__metadata-field">
                    <span className="question-item__metadata-label">
                      {getResourceValue("section_id", "Section ID")}
                    </span>
                    <span className="question-item__metadata-value">
                      {metadataMap[id].sectionId || "—"}
                    </span>
                  </div>
                  <div className="question-item__metadata-field">
                    <span className="question-item__metadata-label">
                      {getResourceValue("country", "Country")}
                    </span>
                    <span className="question-item__metadata-value">
                      {metadataMap[id].country || "—"}
                    </span>
                  </div>
                  <div className="question-item__metadata-field">
                    <span className="question-item__metadata-label">
                      {getResourceValue("subject", "Subject")}
                    </span>
                    <span className="question-item__metadata-value">
                      {metadataMap[id].subject || "—"}
                    </span>
                  </div>
                  <div className="question-item__metadata-field">
                    <span className="question-item__metadata-label">
                      {getResourceValue("grade", "Grade")}
                    </span>
                    <span className="question-item__metadata-value">
                      {metadataMap[id].grade || "—"}
                    </span>
                  </div>
                  <div className="question-item__metadata-field">
                    <span className="question-item__metadata-label">
                      {getResourceValue("language", "Language")}
                    </span>
                    <span className="question-item__metadata-value">
                      {metadataMap[id].language || "—"}
                    </span>
                  </div>
                  <div className="question-item__metadata-field">
                    <span className="question-item__metadata-label">
                      {getResourceValue("question_type", "Question Type")}
                    </span>
                    <span className="question-item__metadata-value">
                      {metadataMap[id].questionTypes || "—"}
                    </span>
                  </div>
                </div>
              )}
              <div className="question-item__content">
                {presignedUrlMap[id] && (
                  <QuestionRenderer
                    key={id}
                    questionId={id}
                    jobId={jobId}
                    packageBasePath={presignedUrlMap[id]}
                    onMetadataLoaded={(metadata) => handleMetadataLoaded(id, metadata)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <PreviewPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title={getResourceValue("delete_questions", "Delete Questions")}
        message={
          isBulkDelete
            ? getResourceValue(
                "confirm_delete_questions",
                `You are about to delete ${selectedQuestionIds.size} question${selectedQuestionIds.size === 1 ? "" : "s"}. This cannot be undone.`
              )
            : getResourceValue(
                "confirm_delete_question",
                "You are about to delete 1 question. This cannot be undone."
              )
        }
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PreviewQuestionsResults;
